import { computed, observable, action, makeObservable } from 'mobx';
import { MultibetPostBodyItemType } from 'src/domains/sportsbook/betting/state/multibetState/Types';
import { MobxValue } from 'src_common/common/mobx-utils/MobxValue';
import {
    MultibetPriceErrorType,
    MultibetPriceSuccessType,
    MultibetPriceSuspendedType,
    MultibetPriceExpiredType,
} from 'src/api/config/eventsMultibet/multibetGetPrice';
import { Common } from 'src/domains/common/Common';
import { TrpcClient } from 'src/appState/TrpcClient';

export type MultibetPriceResourceType =
    | null
    | MultibetPriceErrorType
    | MultibetPriceSuccessType
    | MultibetPriceSuspendedType
    | MultibetPriceExpiredType;

class ConnectWrapper {
    public currentRequest: number = 0;

    private readonly trpcClient: TrpcClient;

    public constructor(
        common: Common,
        public readonly marketId: string,
        public readonly getPostBody: () => Array<MultibetPostBodyItemType>,
        public readonly getStake: () => number,
        public readonly whenLoad: () => void
    ) {
        this.trpcClient = common.trpcClient;
    }

    public connect(self: MobxValue<MultibetPriceResourceType>): ReturnType<typeof setInterval> {
        const refresh = (): void => {
            this.currentRequest++;

            const currentRequest = this.currentRequest;

            const postBody = this.getPostBody();
            if (postBody.length < 2) {
                self.setValue(null);
                this.whenLoad();
                return;
            }

            (async (): Promise<void> => {
                const response = await this.trpcClient.client.betBuilder.getMultibetPrice.mutate({
                    marketId: this.marketId,
                    stake: this.getStake(),
                });

                if (currentRequest === this.currentRequest) {
                    self.setValue(response);
                    this.whenLoad();
                }
            })().catch((error) => {
                console.error('[Multibet] Price fetch error:', error);
                if (currentRequest === this.currentRequest) {
                    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
                    self.setValue({
                        type: 'error',
                        data: {
                            code: 'FETCH_ERROR',
                            errors: [{ code: 'FETCH_ERROR', message: errorMessage }],
                            message: errorMessage,
                        },
                    });
                    this.whenLoad();
                }
            });
        };

        setTimeout(refresh, 0);
        const timer = setInterval(refresh, 1000);
        return timer;
    }

    public dispose(timer: ReturnType<typeof setInterval>, self: MobxValue<MultibetPriceResourceType>): void {
        self.setValue(null);
        clearInterval(timer);
    }
}

export class MultibetPriceResponseItem {
    public readonly valuePrice: MobxValue<MultibetPriceResourceType>;

    @observable public priceThatUserWants: null | number = null;

    public constructor(
        common: Common,
        marketId: string,
        getPostBody: () => Array<MultibetPostBodyItemType>,
        getStake: () => number
    ) {
        makeObservable(this);

        this.valuePrice = MobxValue.create({
            initValue: null,
            connect: new ConnectWrapper(common, marketId, getPostBody, getStake, () => {
                if (this.priceThatUserWants === null) {
                    const value = this.price;
                    if (value !== null && value.type === 'ok') {
                        this.priceThatUserWants = value.data.price;
                    }
                }
            }),
        });
    }

    @action public reset(): void {
        this.valuePrice.setValue(null);
        this.priceThatUserWants = null;
    }

    @computed.struct public get price4(): MultibetPriceResourceType {
        const value = this.valuePrice.getValue();

        if (value === null || value.type === 'error' || value.type === 'suspended' || value.type === 'expired') {
            return value;
        }

        return value;
    }

    @computed.struct public get price(): MultibetPriceResourceType {
        return this.price4;
    }

    public isError(): boolean {
        return this.price !== null && (this.price.type === 'error' || this.price.type === 'expired');
    }

    @action public acceptChanges(): void {
        const value = this.price;
        if (value !== null && value.type === 'ok') {
            this.priceThatUserWants = value.data.price;
        }
    }

    public get selectionChanged(): boolean {
        const value = this.price;

        if (value === null || value.type === 'error' || value.type === 'suspended' || value.type === 'expired') {
            return false;
        }

        const priceThenUserWants = this.priceThatUserWants;

        if (priceThenUserWants === null) {
            return false;
        }

        if (value.data.price === priceThenUserWants) {
            return false;
        }

        return true;
    }

    public get selectionChangedDown(): boolean {
        const value = this.price;

        if (value === null || value.type === 'error' || value.type === 'suspended' || value.type === 'expired') {
            return false;
        }

        const priceThenUserWants = this.priceThatUserWants;

        if (priceThenUserWants === null) {
            return false;
        }

        if (value.data.price < priceThenUserWants) {
            return false;
        }

        return true;
    }

    public dump(): void {
        console.info('[Multibet] Price dump', {
            priceThatUserWants: this.priceThatUserWants,
            selectionChanged: this.selectionChanged,
            price: this.price,
        });
    }
}
